import { useId, useState } from "react";
import { FileText, X } from "lucide-react";

import type { Factura } from "../api/tipos.ts";
import { pesos } from "../formato.ts";
import { candidatasPara, pareceNombre } from "./sugerencia-factura.ts";
import { cx } from "./ui.tsx";

/**
 * Contra qué factura(s) se aplica el pago (SPEC-028 §4).
 *
 * Fichas y no una cadena separada por comas. La cadena sería más fácil de
 * escribir hoy y obligaría a alguien a volver a partirla mañana, adivinando si
 * el separador era la coma, el guion o la palabra «y» — que es exactamente el
 * problema que esta pantalla viene a quitar, un campo más allá.
 *
 * `cartera` son las facturas cargadas. Se usan para sugerir y para
 * marcar cuáles están respaldadas; **si viene vacía no se marca nada**, porque
 * sin cartera todas serían desconocidas y señalarlas todas sería marcar como
 * anómalo el caso normal (§10.1). No se puede decir que algo falta si no hay
 * dónde mirar.
 */
export function CampoFacturas({
  valor,
  alCambiar,
  cartera = [],
  montoCentavos,
  deshabilitado,
}: {
  valor: string[];
  alCambiar: (facturas: string[]) => void;
  cartera?: Factura[];
  /** El importe del pago: pone primero la candidata cuyo total coincide. */
  montoCentavos?: number | null;
  deshabilitado?: boolean;
}) {
  const [texto, setTexto] = useState("");
  const listaId = useId();
  const conocidas = cartera.map((f) => f.numero);
  const hayCartera = conocidas.length > 0;
  const respaldada = (f: string) =>
    conocidas.some((c) => c.trim().toLowerCase() === f.trim().toLowerCase());

  /** Cambia una ficha por la factura que la persona eligió de las sugeridas. */
  const sustituir = (vieja: string, nueva: string) =>
    alCambiar([...new Set(valor.map((v) => (v === vieja ? nueva : v)))]);

  function agregar(bruto: string) {
    const nueva = bruto.trim();
    if (!nueva) return;
    // Se comparan sin distinguir mayúsculas: «fvel 949» y «FVEL 949» son la
    // misma factura, y aceptar las dos crearía un duplicado que el servidor no
    // puede detectar porque para él son cadenas distintas.
    if (!valor.some((v) => v.toLowerCase() === nueva.toLowerCase())) alCambiar([...valor, nueva]);
    setTexto("");
  }

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <FileText
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue-2"
          aria-hidden
        />
        <input
          value={texto}
          disabled={deshabilitado}
          list={hayCartera ? listaId : undefined}
          onChange={(e) => {
            // La coma cierra la ficha: quien viene de escribirlas seguidas no
            // tiene que aprender que aquí se hace de otra forma.
            if (e.target.value.includes(",")) agregar(e.target.value.replace(",", ""));
            else setTexto(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // No envía el formulario: aquí Enter añade una factura más.
              e.preventDefault();
              agregar(texto);
            }
            if (e.key === "Backspace" && !texto && valor.length) alCambiar(valor.slice(0, -1));
          }}
          // Se añade también al salir del campo: pulsar directamente el botón
          // sin haber cerrado la ficha perdería lo escrito sin decir nada.
          onBlur={() => agregar(texto)}
          placeholder={valor.length ? "¿Otra factura?" : "¿Contra qué factura? (opcional)"}
          aria-label="Facturas de este pago"
          className="h-10 w-full rounded-xl border border-borde bg-superficie pr-3.5 pl-10 text-sm placeholder:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        />
        {hayCartera && (
          <datalist id={listaId}>
            {conocidas.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        )}
      </div>

      {valor.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {valor.map((f) => (
            <li key={f}>
              <span
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium",
                  hayCartera && !respaldada(f)
                    ? "border-aviso/35 bg-aviso-suave text-aviso-tinta"
                    : "border-borde bg-superficie-2 text-tinta",
                )}
                // El título explica la marca sin gastar un renglón en cada ficha.
                title={
                  hayCartera && !respaldada(f) ? "No está en tu cartera. Se guarda igual." : undefined
                }
              >
                {f}
                <button
                  type="button"
                  onClick={() => alCambiar(valor.filter((v) => v !== f))}
                  aria-label={`Quitar ${f}`}
                  className="-mr-0.5 rounded p-0.5 opacity-60 hover:opacity-100"
                >
                  <X className="size-3" aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Sugerencias y avisos, uno por ficha que lo necesite. Van debajo de todas
          las fichas y no dentro de cada una: una ficha es una etiqueta, y meterle
          botones la convierte en un formulario diminuto que nadie lee. */}
      {valor.map((f) => {
        const candidatas = hayCartera && !respaldada(f) ? candidatasPara(f, cartera, montoCentavos) : [];
        if (pareceNombre(f)) {
          return (
            <p key={`n-${f}`} className="text-xs text-aviso-tinta">
              «{f}» parece el nombre de un cliente, no una factura: las facturas llevan número. Si es
              el cliente, va en el campo de abajo.
            </p>
          );
        }
        if (!candidatas.length) return null;
        return (
          <div key={`s-${f}`} className="space-y-1 text-xs">
            <p className="text-tenue">
              «{f}» no está en tu cartera.{" "}
              {candidatas.length === 1
                ? "¿Te refieres a esta?"
                : `Hay ${candidatas.length} facturas con ese número. ¿Cuál es?`}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {candidatas.map((c) => (
                <button
                  key={c.numero}
                  type="button"
                  disabled={deshabilitado}
                  onClick={() => sustituir(f, c.numero)}
                  className={cx(
                    "rounded-lg border px-2 py-1 text-left font-medium transition-colors",
                    c.coincideMonto
                      ? "border-exito/40 bg-exito-suave text-exito-tinta hover:border-exito"
                      : "border-borde bg-superficie hover:border-marca",
                  )}
                >
                  <span className="cifra">{c.numero}</span>
                  {c.cliente && <span className="font-normal opacity-80"> · {c.cliente}</span>}
                  {c.totalCentavos != null && (
                    <span className="cifra font-normal opacity-80"> · {pesos(c.totalCentavos)}</span>
                  )}
                  {/* Se dice por qué va primera, en vez de solo ponerla primera. */}
                  {c.coincideMonto && <span className="font-normal"> · coincide el importe</span>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
